import { ReactNode, useEffect, useState } from 'react';
import { Button, Tooltip, message, Popover, Radio } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { What } from 'views/Aide/Scenario/PolicyTable';
import styled from '@emotion/styled';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useAuthorization } from 'components/Authorization';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import useModal from 'hooks/useModal';
import { getHeaderLang } from 'services/api';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useStoreFilter from '../useStoreFilter';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { exportSpsOverview, getSpsOverview } from './service';
import { DEFAULT_PAGE_SIZE, DIMENSION_META, FETCH_ERROR_PHRASE, RANGE_HINT } from './constants';
import SpsOverviewTable from './SpsOverviewTable';
import SpsTrendDrawer from './SpsTrendDrawer';

type RT = Dashboard.TikTokUS.ShopOverviewRow;
type ProblemOrderMetricCode = Dashboard.TikTokUS.ProblemOrderMetricCode;

export interface SpsOverviewProps {
  banner?: ReactNode;
  departments: Dashboard.Department[];
  /** 指标列【明细】下钻：切到「问题订单明细」并带入店铺 + 指标 */
  onCheckProblemOrder: (record: RT, metricCode: ProblemOrderMetricCode) => void;
  /** AHT 列【明细】下钻：切到「售后处理明细」并带入店铺 */
  onCheckAftersales: (record: RT) => void;
  /** IM_DSAT 列【明细】下钻：切到「DSAT明细」并带入店铺 */
  onCheckDsat: (record: RT) => void;
}

/**
 * 排行榜（店铺表现）
 * - 不接受时间范围，展示最新一轮快照（接口 3.1）
 * - 3 个维度 Tab：切 Tab 只换后两列，**不重新请求**
 * - 仅 SPS 总分列可排序（三态），排序由服务端全局完成，前端不本地排序
 */
function SpsOverview({
  banner,
  departments,
  onCheckProblemOrder,
  onCheckAftersales,
  onCheckDsat,
}: SpsOverviewProps) {
  const { t } = useTranslate();

  const exportable = useGetPermission()('导出平台数据');
  const auth = useAuthorization<Oversea.Token.Payload>();
  const systemLanguage = useSystemLanguageValue();
  const { run: onDownload } = useExportFile();

  const {
    selectedDepartments,
    departmentsSelect,
    storeType,
    storeTypeSelect,
    storeInfo,
    storeInfoSelect,
  } = useStoreFilter({ platform: 'TIKTOK_US', departments });

  const [dimension, setDimension] =
    useState<Dashboard.TikTokUS.DimensionName>('Product satisfaction');
  /** 三态：undefined = 接口默认排序（近 7 天订单量降序）→ DESC → ASC */
  const [sortOrder, setSortOrder] = useState<Dashboard.TikTokUS.SortOrder | undefined>();

  const rangeParams = (): Dashboard.TikTokUS.RangeFilter => ({
    platform: 'TIKTOK_US',
    departmentIds: selectedDepartments.map(({ id }) => id),
    storeType: storeType === 'CROSS' ? '跨境' : storeType === 'LOCAL' ? '本土' : undefined,
    storeIds: storeInfo.list?.map(({ id }) => Number(id)),
  });

  const { loading, data, run } = useRequest(
    (page: { pageNum: number; pageSize: number } = { pageNum: 1, pageSize: DEFAULT_PAGE_SIZE }) =>
      getSpsOverview({
        ...rangeParams(),
        pageNum: page.pageNum,
        pageSize: page.pageSize,
        // ⚠️ 目前仅支持按 SPS 总分排序；不传 sortBy 时 sortOrder 无意义
        sortBy: sortOrder ? 'SPS_SCORE' : undefined,
        sortOrder,
      }),
    {
      manual: true,
      // 接口失败统一报错，不透传后端 msg
      onError: () => message.error(t(FETCH_ERROR_PHRASE)),
    },
  );

  useEffect(() => {
    run({ pageNum: 1, pageSize: DEFAULT_PAGE_SIZE });
  }, [run, sortOrder, selectedDepartments, storeType, storeInfo]);

  const { visible, show, hide, meta } = useModal<RT>();

  /**
   * 关抽屉后**重新拉一次列表**：抽屉里展示的是同一店铺的实时快照，
   * 关掉时榜单上的总分/分档应该跟着刷新（保持当前页码，不把用户弹回第一页）。
   */
  const handleCloseDrawer = () => {
    hide();
    run({
      pageNum: data?.pageNum || 1,
      pageSize: data?.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const { config: storeNameConfig } = useStoreNameConfig();

  /** 抽屉标题里的店铺名：按 isStoreAliasFirst 决定用别名还是店名 */
  const drawerStoreLabel = meta
    ? isStoreAliasFirst(storeNameConfig, 'TIKTOK_US', meta.region)
      ? meta.storeAlias || meta.storeName
      : meta.storeName
    : undefined;

  const handleExport = () => {
    onDownload(() =>
      exportSpsOverview({
        ...rangeParams(),
        language: getHeaderLang(systemLanguage),
        userId: auth?.payload?.id,
      }),
    );
  };

  /** 统计区间按 PRD 模板：T = 今天，恒定两行（60 天组 / 30 天组） */
  const dateRender = (x: number) => dayjs().subtract(x, 'days').format('YYYY/MM/DD');

  /** 最新一轮的数据更新时间（同一轮里所有店铺一致，取首行即可） */
  const dataUpdateTime = data?.list?.[0]?.dataUpdateTime;

  const filter = (
    <header>
      <section>
        {departmentsSelect}
        {storeTypeSelect}
        {storeInfoSelect}
      </section>
      <div className="flex items-center">
        {t('数据说明')}
        <Popover
          trigger={['hover']}
          placement="topRight"
          content={
            <div className="max-w-[562px]">
              <div>
                ·{' '}
                {t(
                  '数据来源为 TikTok Shop 官方 SPS（店铺表现分），仅支持美区店铺；本页展示最新一轮计算结果，不支持选择日期。',
                )}
              </div>
              <div>
                ·{' '}
                {t(
                  '各指标的评估窗口长度不同（60 天 / 30 天），实际区间以本页顶部说明为准；查看历史趋势请进入单店铺详情。',
                )}
              </div>
            </div>
          }
        >
          <What />
        </Popover>
        {exportable && (
          <Tooltip title={t('导出')}>
            <Button className="ml-4" icon={<DownloadOutlined />} onClick={handleExport} />
          </Tooltip>
        )}
      </div>
    </header>
  );

  return (
    <Container>
      {filter}
      {banner}
      <section>
        <div className="meta-area">
          {/* 数据新鲜度 + 统计区间（PRD 4.1.7 / 4.6）：恒定两行，T = 今天；各指标窗口长度不同 */}
          {/*
            ⚠️ 这里必须用 JS 拼接而不是 t('…：x-y', { x, y })：
            未注册的 key 会被 tolgee 原样返回且**不做参数插值**，页面上会直接显示字面的 `x-y`
            （已踩过）。所以日期留在 JS 里拼，key 只放标签本身。
          */}
          <div style={{ fontSize: 12, color: 'var(--fett-text-tertiary)', marginBottom: 8 }}>
            <div>{`${t('数据更新于：')}${dataUpdateTime || '-'}`}</div>
            <div>{`${t(RANGE_HINT.sixtyDays)}：${dateRender(61)} - ${dateRender(2)}`}</div>
            <div>{`${t(RANGE_HINT.thirtyDays)}：${dateRender(31)} - ${dateRender(2)}`}</div>
          </div>
          <Radio.Group value={dimension} onChange={({ target: { value } }) => setDimension(value)}>
            {DIMENSION_META.map(({ value, label }) => (
              <Radio.Button key={value} value={value}>
                {t(label)}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
        <div className="table-area">
          <SpsOverviewTable
            dimension={dimension}
            loading={loading}
            dataSource={data?.list}
            pagination={{
              current: data?.pageNum || 1,
              pageSize: data?.pageSize || DEFAULT_PAGE_SIZE,
              total: data?.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (x) => t('feedback.共x项', { x }),
            }}
            onChange={(page: any, _filters: any, sorter: any) => {
              const samePage =
                page.current === (data?.pageNum || 1) &&
                page.pageSize === (data?.pageSize || DEFAULT_PAGE_SIZE);
              if (samePage) {
                setSortOrder(
                  sorter?.order === 'ascend'
                    ? 'ASC'
                    : sorter?.order === 'descend'
                    ? 'DESC'
                    : undefined,
                );
              } else {
                run({ pageNum: page.current, pageSize: page.pageSize });
              }
            }}
            onCheckTrend={show}
            onCheckProblemOrder={onCheckProblemOrder}
            onCheckAftersales={onCheckAftersales}
            onCheckDsat={onCheckDsat}
          />
        </div>
      </section>
      {meta && (
        <SpsTrendDrawer
          key={meta.storeId}
          open={visible}
          storeId={meta.storeId}
          storeLabel={drawerStoreLabel}
          // 抽屉的维度 Tab 默认跟榜单当前 Tab 一致
          initDimension={dimension}
          onClose={handleCloseDrawer}
        />
      )}
    </Container>
  );
}

/**
 * 本页自带容器（不复用 ShopeePerformance.Container）：
 * 排行榜在表格上方还有「统计区间说明 + 维度 Tab」两块固定内容，
 * 需要用 flex 列布局把表格区压成 `flex:1 + min-height:0`，否则 ScrollTable 的 `height:100%` 会溢出。
 */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  > header {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    margin-bottom: 16px;
    margin-top: 1px;
    background: var(--fett-bg-panel);
    > section {
      flex: 1 0 0;
      display: flex;
      align-items: center;
      > * {
        margin-right: 16px;
      }
    }
  }
  > .ant-alert {
    flex: 0 0 auto;
    background-color: var(--fett-surface-warning-border);
    button {
      height: 17px;
      margin-top: -4px;
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 16px;
    background: var(--fett-bg-panel);
    > .meta-area {
      flex: 0 0 auto;
      /* 统计区间说明 + 维度 Tab 作为一个整体，与表格之间留出间距 */
      margin-bottom: 16px;
    }
    > .table-area {
      flex: 1 0 0;
      min-height: 0;
    }
  }
`;

export default SpsOverview;
